import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";
import {useDispatch} from "react-redux";
import {Storage} from "aws-amplify";
import {useTranslation} from "react-i18next";
// Quill
import Quill from "quill";
import ReactQuill from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import "react-quill/dist/quill.snow.css";
// Styles & Components
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Divider from "connectui/component/display/Divider";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import EmailRecipients from "./EmailRecipients";
import LoadingBar from "connectui/component/display/LoadingBar";
import Container from "connectui/component/layout/Container";
import InputBase from "connectui/component/input/text/InputBase";
// Redux & Utils
import {newUuid} from "utilities/helperFunctions";
import {useImageCompressor} from "utilities/fileUtilities";
import config from "configuration/settings.js";
import {
  closeGeneralError,
  openGeneralError,
} from "redux/actions/settingsActions";

Quill.register("modules/imageResize", ImageResize);

const useStyles = (theme) =>
  buildStyles({
    root: {
      width: "100%",
      height: "100%",
      overflow: "auto",
      flexGrow: 1,
      "& .quill": {
        width: "100%",
        height: "100%",
      },
      "& .quill .ql-container": {
        border: "none",
      },
      "& .quill .ql-tooltip": {left: "0px !important"},
      "& .quill .ql-editor": {
        padding: theme.spacing(3, 4, 0),
        overflow: "visible",
      },
      // Toolbar
      "& #quill-toolbar": {padding: theme.spacing(0, 4)},
      "& #quill-toolbar button": {
        width: "16px",
        height: "16px",
        padding: 0,
      },
      "& #quill-toolbar button svg": {filter: "invert(0.4)"},
      "& #quill-toolbar .ql-font": {
        height: "28px",
        width: "90px",
        marginRight: theme.spacing(1),
      },
      "& #quill-toolbar .ql-size": {
        height: "28px",
        width: "70px",
      },
      "& #quill-toolbar .ql-color, #quill-toolbar .ql-background, #quill-toolbar .ql-align":
        {
          width: "18px",
          height: "18px",
          marginRight: theme.spacing(1),
          paddingBottom: theme.spacing(0.5),
        },
      "& #quill-toolbar .ql-color": {height: "21px"},
      "& #quill-toolbar .ql-picker-label": {
        padding: 0,
        display: "flex",
        alignItems: "center",
        filter: "invert(0.4)",
        height: "fit-content",
      },
    },
  });

const modules = {
  toolbar: {
    container: "#quill-toolbar-conversation",
  },
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
  imageResize: {
    parchment: Quill.import("parchment"),
    modules: ["Resize", "DisplaySize"],
  },
};

const CustomToolbar = () => (
  <div
    id="quill-toolbar-conversation"
    style={{
      width: "100%",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "flex-start",
      border: "none",
      marginBottom: "8px",
    }}
  >
    <select className="ql-font"></select>
    <select className="ql-size"></select>
    <Divider orientation="vertical" sx={{height: "16px"}} />
    <button className="ql-bold"></button>
    <button className="ql-italic"></button>
    <button className="ql-underline"></button>
    <button className="ql-strike"></button>
    <select className="ql-color"></select>
    <select className="ql-background"></select>
    <Divider orientation="vertical" sx={{height: "16px"}} />
    <select className="ql-align"></select>
    <button className="ql-list" value="ordered"></button>
    <button className="ql-list" value="bullet"></button>
    <button className="ql-indent" value="-1"></button>
    <button className="ql-indent" value="+1"></button>
    <Divider orientation="vertical" sx={{height: "16px"}} />
    <button className="ql-blockquote"></button>
    <button className="ql-link"></button>
    <button className="ql-image"></button>
    <button className="ql-video"></button>
    <Divider orientation="vertical" sx={{height: "16px"}} />
    <button className="ql-undo">
      <SvgIcon component={ICONS.undo} sx={{width: "14px", height: "14px"}} />
    </button>
    <button className="ql-redo">
      <SvgIcon
        component={ICONS.forward}
        sx={{width: "14px", height: "13px", marginTop: "-1px"}}
      />
    </button>
    <button className="ql-clean"></button>
  </div>
);

const EmailInput = forwardRef(
  ({email, onKeyDown, onChange, imagePath}, ref) => {
    const {theme} = useTheme();
    const dispatch = useDispatch();
    const classes = useStyles(theme);
    const quillRef = useRef();
    const imgInputRef = useRef();
    const {t} = useTranslation();
    const [showVideoPrompt, setShowVideoPrompt] = useState(false);
    const [compressEnabled, setCompressEnabled] = useState(false);
    const [progressObj, setProgressObj] = useState({loaded: 0, total: 100});
    const [imgToUpload, setImgToUpload] = useState(null);
    const [loaderOpen, setLoaderOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      getEditor: () => quillRef.current?.getEditor?.(),
      getEditorRoot: () => quillRef.current?.getEditor?.()?.root,
      focus: () => quillRef.current?.focus?.(),
    }));

    useEffect(() => {
      if (imgToUpload && !compressEnabled) {
        setCompressEnabled(true);
      }
    }, [imgToUpload]);

    // ----- HANDLING IMAGES - START
    useImageCompressor({
      srcImgs: !!compressEnabled && !!imgToUpload ? [imgToUpload] : [],
      onSuccess: (outputImgs) => onCompressSuccess(outputImgs),
    });

    const onCompressSuccess = (outputImgs) => {
      setCompressEnabled((prev) => false);
      setImgToUpload((prev) => null);
      handleUploadImage(outputImgs[0]);
    };

    const uploadImageToS3 = async (file) => {
      setProgressObj((prev) => ({loaded: 0, total: 100}));
      setLoaderOpen((prev) => true);

      const imageName = `${imagePath}/img-${newUuid()}`;
      try {
        await Storage.put(imageName, file, {
          contentType: file.type,
          progressCallback(progress) {
            setProgressObj((prev) => ({
              loaded: progress.loaded,
              total: progress.total,
            }));
          },
        });
        const link = `${config.s3.URL}${imageName}`;
        setLoaderOpen((prev) => false);
        return link;
      } catch (error) {
        console.log("error uploading image", error);
        handleError();
        setLoaderOpen((prev) => false);
        return null;
      }
    };

    const handleUploadImage = async (file) => {
      const quill = quillRef.current;
      if (!quill) return;

      const position = quill.editor.getSelection().index;
      const imageUrl = await uploadImageToS3(file);
      await insertImageToEditor(imageUrl, position);
    };

    const handleSelectImage = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setLoaderOpen(true);
      setProgressObj({loaded: 0, total: 100});
      setImgToUpload(file);
    };

    const insertImageToEditor = async (imageUrl, position) => {
      const quill = quillRef.current;
      if (!quill || !imageUrl) return;

      try {
        await quill.editor.insertEmbed(position, "image", imageUrl);
        await quill.editor.insertText(position + 1, "\n");
        await quill.editor.setSelection(position + 2);
      } catch (error) {
        console.log("error inserting image to editor", error);
        handleError();
      }
    };

    const handleError = () => {
      const message = t("inbox.conversations.email.image-error.title");
      const subtitle = t("inbox.conversations.email.image-error.subtitle");
      dispatch(
        openGeneralError({
          open: true,
          message: message,
          disableSeverity: true,
          subtitle: subtitle,
          duration: 6000,
          onClose: () => dispatch(closeGeneralError()),
        }),
      );
    };
    // ----- HANDLING IMAGES - END

    const handleHistoryAction = (action) => {
      const history = quillRef.current?.editor?.history;
      if (!history) return;
      if (action === "undo") history.undo();
      if (action === "redo") history.redo();
    };

    const insertImage = () => imgInputRef.current?.click();
    const insertVideo = () => setShowVideoPrompt(true);

    modules.toolbar.handlers = {
      undo: () => handleHistoryAction("undo"),
      redo: () => handleHistoryAction("redo"),
      image: insertImage,
      video: insertVideo,
    };

    const handleContentChange = (val) => {
      onChange({html: val});
    };

    return (
      <FlexContainer
        flexDirection="column"
        id="react-quill-wrapper"
        className={classes.root}
      >
        {loaderOpen && (
          <Container
            sx={{
              width: "100%",
              padding: theme.spacing(0, 4),
              marginBottom: theme.spacing(3),
            }}
          >
            <LoadingBar
              progress={Math.ceil(
                (progressObj.loaded * 100) / progressObj.total,
              )}
            />
          </Container>
        )}
        {/* RECIPIENTS */}
        <EmailRecipients email={email} setEmail={onChange} />
        {/* EDITOR */}
        <InputBase
          ref={imgInputRef}
          sx={{display: "none"}}
          inputProps={{
            id: "imageAttachmentInput",
            type: "file",
            accept: "image/*",
            onChange: handleSelectImage,
          }}
        />
        <CustomToolbar />
        <ReactQuill
          ref={quillRef}
          value={email.html}
          modules={modules}
          bounds="#react-quill-wrapper"
          placeholder={"Content email"}
          onChange={handleContentChange}
          onKeyDown={onKeyDown}
        />
      </FlexContainer>
    );
  },
);

export default EmailInput;
